import { Flex, Input, Form, Button, Card, theme, message } from 'antd';
import { Rule } from 'antd/es/form';
import { useContext, useEffect, useState } from 'react';
import { SignUpParams, useClient } from '../client';
import { AxiosError, CanceledError } from 'axios';
import { AppContext } from '../context';
import { useNavigate } from 'react-router-dom';

const nameRules: Rule[] = [{ required: true, message: '${label} is required' }];

const emailRules: Rule[] = [
  { required: true, message: '${label} is required' },
  { type: 'email', message: 'Valid email is required' },
];

const passwordRules: Rule[] = [
  { required: true, message: '${label} is required' },
  { min: 8 },
];

const password2Rules: Rule[] = [
  { required: true, message: '${label} is required' },
  ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue('password') === value) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('Passwords do not match'));
    },
  }),
];

type SignUpFormData = {
  user_name: string;
  email: string;
  password: string;
  password2: string;
};

type SignUpErrorData = {
  user_name?: string;
  email?: string;
  password?: string;
  [key: string]: string | undefined;
};

export default function SignUp() {
  const { token: themeToken } = theme.useToken();
  const [processing, setProcessing] = useState(false);
  const [signUpParams, setSignUpParams] = useState<SignUpParams>();
  const [errorData, setErrorData] = useState<SignUpErrorData>({});
  const [messageApi, contextHolder] = message.useMessage();
  const { setUser } = useContext(AppContext);
  const navigate = useNavigate();
  const client = useClient(null);

  useEffect(() => {
    if (!processing || !signUpParams) return;
    (async () => {
      try {
        const user = await client.signUp(signUpParams);
        setUser(user);
        navigate('');
      } catch (error) {
        if (error instanceof CanceledError) return;
        if (error instanceof AxiosError && error.status === 400) {
          const errDetail: SignUpErrorData | undefined =
            error.response?.data?.detail;
          setErrorData(errDetail || {});
        } else {
          messageApi.error('Sign up error');
          console.log('Sign up error:', error);
        }
      }
      setProcessing(false);
    })();
  }, [processing, messageApi, signUpParams, setUser, navigate, client]);

  const makeClearError = (field: string) => {
    return () => {
      setErrorData(({ [field]: _, ...rest }) => rest);
    };
  };

  return (
    <Flex
      className="auth-container"
      justify="center"
      align="center"
      style={{ backgroundColor: themeToken.colorPrimary }}
    >
      {contextHolder}
      <div>
        <Card>
          <Form
            className="sign-up-form"
            onFinish={(formData: Record<string, any>) => {
              const { email, password, user_name } = formData as SignUpFormData;
              setSignUpParams({ email, password, user_name });
              setProcessing(true);
            }}
          >
            <Form.Item
              validateStatus={errorData.user_name ? 'error' : undefined}
              help={errorData.user_name}
              key={'param-name'}
              name="user_name"
              label="Name"
              rules={nameRules}
              required={false}
            >
              <Input
                onChange={makeClearError('user_name')}
                autoComplete="name"
              />
            </Form.Item>
            <Form.Item
              key={'param-email'}
              name="email"
              label="Email"
              rules={emailRules}
              required={false}
              validateStatus={errorData.email ? 'error' : undefined}
              help={errorData.email}
            >
              <Input autoComplete="email" onChange={makeClearError('email')} />
            </Form.Item>
            <Form.Item
              key={'param-token'}
              name="password"
              label="Password"
              rules={passwordRules}
              required={false}
              validateStatus={errorData.password ? 'error' : undefined}
              help={errorData.password}
            >
              <Input.Password
                autoComplete="password"
                onChange={makeClearError('password')}
              />
            </Form.Item>
            <Form.Item
              key={'param-password2'}
              name="password2"
              label="Confirm password"
              rules={password2Rules}
              required={false}
            >
              <Input.Password autoComplete="password" />
            </Form.Item>
            <Form.Item className="sign-up-form-button">
              <Button type="primary" htmlType="submit" loading={processing}>
                Sign Up
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </Flex>
  );
}
